import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import Icon from '@mdi/react';
import { mdiChevronRight, mdiChevronLeft } from '@mdi/js';
import { STATUS_COLOR, STATUS_BORDER_COLOR, leftColumns } from '../constant';
import {
  FilterDropdown,
  TableSortDisplayArrow,
  SpinnerLoading,
} from '../../../Common';

import './HolidayAnalysisCalendarMonthly.scss';

export default function HolidayAnalysisCalendarMonthly({
  holidayData = [],
  year,
  month,
  handleMonthNext,
  handleMonthPrev,
  pageRequest,
  setColumnName,
  columnData,
  applyFilter,
  columnName,
  setPageRequest,
  handleManualSort,
  checkColumnIfSorted,
  isLoadingData,
  isLoadingColumn,
  handleAutoSort,
}) {
  // 1) State & query hooks
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [currentMonth, setCurrentMonth] = useState(moment([2025, 1]));
  useEffect(() => {
    setCurrentMonth(moment([year, month]));
  }, [year, month]);

  // Destructure of your arrays (removed unused weekendRecords)
  const [leaveRecords = [], , holidayRecords = []] = holidayData?.dataset || [];

  const holidayDatesMap = useMemo(() => {
    const m = {};
    holidayRecords.forEach(({ Yr, Mm, Dd, HolidayDesc }) => {
      m[`${Yr}-${Mm}-${Dd}`] = HolidayDesc.trim();
    });
    return m;
  }, [holidayRecords]);

  // Build rows from leaveRecords
  const rows = useMemo(() => {
    const empMap = {};
    leaveRecords.forEach(rec => {
      const id = rec?.EmpCd?.trim();
      if (!empMap[id]) {
        empMap[id] = {
          department: rec?.Dept?.trim(),
          section: rec?.Section?.trim(),
          employee: rec?.Name?.trim(),
          leaves: [],
        };
      }
      const from = moment(rec?.LvApprFromDt);
      const to = moment(rec?.LvApprToDt);
      const type = rec?.LeaveDesc?.trim();
      for (let m = from.clone(); m.isSameOrBefore(to); m.add(1, 'day')) {
        empMap[id].leaves.push({
          date: m.format('YYYY-MM-DD'),
          type,
        });
      }
    });
    return Object.values(empMap);
  }, [leaveRecords]);

  // Get the dates of each day in the current month
  const dates = useMemo(() => {
    const arr = [];
    const cnt = currentMonth.daysInMonth();
    for (let d = 1; d <= cnt; d += 1) {
      arr.push(currentMonth.clone().date(d));
    }
    return arr;
  }, [currentMonth]);

  // Group rows by department
  const groupedByDepartment = useMemo(
    () =>
      rows.reduce((acc, row) => {
        (acc[row.department] ||= []).push(row);
        return acc;
      }, {}),
    [rows],
  );

  // Handler collapse/expand of single department
  const toggleCollapse = dept =>
    setCollapsedDepartments(prev => ({
      ...prev,
      [dept]: !prev[dept],
    }));

  // Check if all departments are collapsed
  const allDepartments = Object.keys(groupedByDepartment);
  const allCollapsed =
    allDepartments.length > 0 &&
    allDepartments.every(dept => collapsedDepartments[dept]);

  // Loading state
  if (isLoadingData) {
    return <SpinnerLoading spinnerSize="2rem" height="60vh" />;
  }

  // Render
  return (
    <div className="holiday-calendar-container monthly">
      <div className="table-scroll">
        <table className="holiday-calendar-table">
          <colgroup>
            {leftColumns.map((_, i) => (
              <col className="first-row-left-header" key={i} />
            ))}
            {dates.map((_, i) => (
              <col
                key={i}
                style={{
                  width: `calc((100% - ${leftColumns.length * 110}px) / ${dates.length})`,
                }}
              />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th colSpan={leftColumns.length} className="left-header top-cell">
                <div className="d-flex align-items-center gap-2 px-3">
                  <button
                    type="button"
                    className="all-collapse-toggle p-0"
                    onClick={() => {
                      if (allCollapsed) {
                        setCollapsedDepartments({});
                      } else {
                        const collapsed = {};
                        allDepartments.forEach(dept => {
                          collapsed[dept] = true;
                        });
                        setCollapsedDepartments(collapsed);
                      }
                    }}
                  >
                    {allCollapsed ? '[+]' : '[-]'}
                  </button>
                </div>
              </th>
              <th colSpan={dates.length} className="top-month-nav">
                <div className="d-flex align-items-center justify-content-center gap-4">
                  <span onClick={handleMonthPrev} className="cursor-pointer">
                    <span className="next-prev-month-btn d-flex align-items-center justify-content-center">
                      <Icon
                        path={mdiChevronLeft}
                        size={1}
                        className="next-prev-month-btn-icon"
                      />
                    </span>
                  </span>
                  <span className="month-navigation">
                    {currentMonth.format('MMMM YYYY')}
                  </span>
                  <span onClick={handleMonthNext} className="cursor-pointer">
                    <span className="next-prev-month-btn d-flex align-items-center justify-content-center">
                      <Icon
                        path={mdiChevronRight}
                        size={1}
                        className="next-prev-month-btn-icon"
                      />
                    </span>
                  </span>
                </div>
              </th>
            </tr>
            <tr>
              {leftColumns.map(col => (
                <th key={col.accessor} className="left-header">
                  <div className="d-flex flex-row align-items-center justify-content-center gap-1">
                    <div
                      className="cursor-pointer"
                      onClick={() => handleAutoSort(col?.key)}
                    >
                      {col.header}
                    </div>
                    <TableSortDisplayArrow
                      direction={checkColumnIfSorted(col?.key)}
                    />
                    <FilterDropdown
                      handleSetColumnName={() => {
                        setColumnName(col?.key);
                      }}
                      setColumnName={setColumnName}
                      columnData={columnData}
                      applyFilter={applyFilter}
                      columnName={columnName}
                      pageRequest={pageRequest}
                      setPageRequest={setPageRequest}
                      handleManualSort={handleManualSort}
                      isLoading={isLoadingColumn}
                    />
                  </div>
                </th>
              ))}
              {dates.map(date => (
                <th key={date.format('YYYY-MM-DD')} className="date-header">
                  {date.date()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Object.entries(groupedByDepartment).map(([dept, deptRows]) => (
              <React.Fragment key={dept}>
                <tr
                  className="department-row"
                  onClick={() => toggleCollapse(dept)}
                >
                  <td colSpan={leftColumns.length + dates.length}>
                    <div className="d-flex justify-content-start align-items-center px-3">
                      <span className="collapse-toggle">
                        [{collapsedDepartments[dept] ? '+' : '-'}]
                      </span>
                      <strong>{dept}</strong>
                    </div>
                  </td>
                </tr>
                {!collapsedDepartments[dept] &&
                  deptRows.map((row, ri) => (
                    <tr key={ri}>
                      {leftColumns.map(col => (
                        <td className="left-data" key={col.accessor}>
                          {!col?.isBlank && row[col.accessor]}
                        </td>
                      ))}

                      {/* {dates.map(dt => {
                        const ds = dt.format('YYYY-MM-DD');
                        const isWeekend = [0, 6].includes(dt.day());
                        // only pick up a leaveEntry if it's not a weekend
                        const leaveEntry =
                          !isWeekend && row.leaves.find(l => l.date === ds);
                        const isHoliday = Boolean(holidayDatesMap[ds]);

                        let bg = '';
                        let title = '';
                        let borderColor = '';

                        if (leaveEntry) {
                          if (
                            leaveEntry.type
                              .toLowerCase()
                              .includes('annual leave planning')
                          ) {
                            bg = STATUS_COLOR.planned;
                            borderColor = STATUS_BORDER_COLOR.planned;
                          } else if (
                            leaveEntry.type
                              .toLowerCase()
                              .includes('annual leave')
                          ) {
                            bg = STATUS_COLOR['annual leave'];
                            borderColor = STATUS_BORDER_COLOR['annual leave'];
                          } else if (
                            leaveEntry.type.toLowerCase().includes('sick leave')
                          ) {
                            bg = STATUS_COLOR['sick leave'];
                            borderColor = STATUS_BORDER_COLOR['sick leave'];
                          } else {
                            bg = STATUS_COLOR['other leave'];
                            borderColor = STATUS_BORDER_COLOR['other leave'];
                          }
                          title = `${leaveEntry.type} on ${dt.format('MMM D, YYYY')}`;
                        } else if (isHoliday) {
                          bg = STATUS_COLOR.holiday;
                          borderColor = STATUS_BORDER_COLOR.holiday;
                          title = holidayDatesMap[ds];
                        } else if (isWeekend) {
                          bg = STATUS_COLOR.weekend;
                          borderColor = STATUS_COLOR.weekend;
                          title = 'Weekend';
                        }

                        return (
                          <td
                            className="right-cell"
                            key={ds}
                            style={{
                              cursor: leaveEntry ? 'help' : 'default',
                              backgroundColor: isWeekend && bg,
                            }}
                            title={title}
                          >
                            {(isHoliday || leaveEntry) && (
                              <div
                                className="highlighted-cell"
                                style={{
                                  backgroundColor: bg,
                                  border: `1.5px solid ${borderColor}`,
                                }}
                              />
                            )}
                          </td>
                        );
                      })} */}

                      {dates.map(dt => {
                        const ds = dt.format('YYYY-MM-DD');
                        const isWeekend = [0, 6].includes(dt.day());

                        // ✅ collect ALL leaves on this day
                        const leavesOnDay = !isWeekend
                          ? row.leaves.filter(l => l.date === ds)
                          : [];

                        const isHoliday = Boolean(holidayDatesMap[ds]);

                        // decide cell style based on highest-priority leave (if multiple)
                        const pickStyleForType = t => {
                          const type = t.toLowerCase();
                          if (type.includes('sick leave')) {
                            return {
                              bg: STATUS_COLOR['sick leave'],
                              borderColor: STATUS_BORDER_COLOR['sick leave'],
                            };
                          }
                          if (type.includes('annual leave planning')) {
                            return {
                              bg: STATUS_COLOR.planned,
                              borderColor: STATUS_BORDER_COLOR.planned,
                            };
                          }
                          if (type.includes('annual leave')) {
                            return {
                              bg: STATUS_COLOR['annual leave'],
                              borderColor: STATUS_BORDER_COLOR['annual leave'],
                            };
                          }
                          return {
                            bg: STATUS_COLOR['other leave'],
                            borderColor: STATUS_BORDER_COLOR['other leave'],
                          };
                        };

                        // priority order if multiple types overlap
                        const PRIORITY = [
                          'sick leave',
                          'annual leave planning',
                          'annual leave',
                          'other leave',
                        ];

                        let bg = '';
                        let borderColor = '';
                        let title = '';

                        if (leavesOnDay.length) {
                          // choose the highest-priority type for the cell color/border
                          const chosenType =
                            PRIORITY.find(p =>
                              leavesOnDay.some(l =>
                                l.type.toLowerCase().includes(p),
                              ),
                            ) || 'other leave';
                          ({ bg, borderColor } = pickStyleForType(chosenType));

                          // 🧾 build a multiline tooltip listing all leaves on that date
                          title = leavesOnDay
                            .map(
                              l => `${l.type} on ${dt.format('MMM D, YYYY')}`,
                            )
                            .join('\n');
                        } else if (isHoliday) {
                          bg = STATUS_COLOR.holiday;
                          borderColor = STATUS_BORDER_COLOR.holiday;
                          title = holidayDatesMap[ds];
                        } else if (isWeekend) {
                          bg = STATUS_COLOR.weekend;
                          borderColor = STATUS_COLOR.weekend;
                          title = 'Weekend';
                        }

                        return (
                          <td
                            className="right-cell"
                            key={ds}
                            style={{
                              cursor: leavesOnDay.length ? 'help' : 'default',
                              backgroundColor: isWeekend && bg,
                            }}
                            title={title}
                          >
                            {(isHoliday || leavesOnDay.length > 0) && (
                              <div
                                className="highlighted-cell"
                                style={{
                                  backgroundColor: bg,
                                  border: `1.5px solid ${borderColor}`,
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
